:root {
  --text: #4a4757;
  --text-h: #16141d;
  --bg: #f3f4f7;
  --card: #ffffff;
  --border: #e3e2e8;
  --muted: #8b8896;
  --accent: #e4572e;
  --accent-bg: rgba(228, 87, 46, 0.1);
  --accent-border: rgba(228, 87, 46, 0.45);
  --ink: #2b2d42;
  --shadow: rgba(22, 20, 29, 0.06) 0 1px 2px, rgba(22, 20, 29, 0.08) 0 8px 24px -8px;

  --sans: system-ui, 'Segoe UI', Roboto, sans-serif;
  --display: 'Bebas Neue', Impact, 'Arial Narrow', sans-serif;
  --mono: ui-monospace, Consolas, monospace;

  font: 16px/145% var(--sans);
  color-scheme: light;
  color: var(--text);
  background: var(--bg);
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  margin: 0;
  background: var(--bg);
}

#root {
  min-height: 100svh;
}

h2,
h3 {
  color: var(--text-h);
  margin: 0;
}

h2 {
  font-family: var(--display);
  font-weight: 400;
  font-size: 30px;
  letter-spacing: 0.5px;
  line-height: 1;
  margin-bottom: 16px;
}

p {
  margin: 0;
}

a {
  color: var(--accent);
}

/* ---------- Cards ---------- */

.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  padding: 24px;
  text-align: left;
  min-width: 0;
}

/* ---------- Header / Gary ---------- */

.site-header {
  display: grid;
  grid-template-columns: 400px minmax(0, 1fr);
  gap: 32px;
  align-items: center;
}

.brand {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.header-report {
  min-width: 0;
}

.brand .logo {
  width: 100%;
  max-width: 400px;
}

.gary {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.gary img {
  width: 120px;
  height: auto;
  mix-blend-mode: multiply;
}

.speech {
  position: relative;
  background: var(--ink);
  color: #fff;
  border-radius: 16px;
  padding: 14px 18px;
  margin-top: 24px;
  flex: 0 1 230px;
  min-width: 0;
  min-height: 64px;
  display: flex;
  align-items: center;
  box-shadow: var(--shadow);
}

.speech::before {
  content: "";
  position: absolute;
  left: -10px;
  top: 22px;
  border-width: 10px 12px 10px 0;
  border-style: solid;
  border-color: transparent var(--ink) transparent transparent;
}

.slogan {
  font-style: italic;
  font-size: 18px;
  line-height: 1.3;
  transition: opacity 600ms ease, transform 600ms ease;
}

.slogan.out {
  opacity: 0;
  transform: translateY(4px);
}

.tech-list {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--text);
  overflow-wrap: anywhere;
}

.tech-list i {
  color: var(--muted);
}

/* ---------- Collapsible panels ---------- */

.collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.collapse-toggle h2 {
  margin: 0;
}

.collapse-toggle .chevron {
  transition: transform 300ms ease;
  color: var(--muted);
}

.collapse-toggle[aria-expanded="true"] .chevron {
  transform: rotate(180deg);
}

/* ---------- Calendar strip ---------- */

.cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.cal-head h2 {
  margin: 0;
}

.cal-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cal-viewport {
  overflow: hidden;
  touch-action: pan-y;
}

.cal-track {
  display: flex;
  gap: var(--cal-gap);
  transition: transform 550ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.month {
  flex: 0 0 var(--cal-month);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  background: #fbfbfd;
}

.month.current {
  border-color: var(--accent-border);
  background: #fff;
}

.month.future {
  opacity: 0.6;
}

.month h3 {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 8px;
}

.month h3 small {
  color: var(--muted);
  font-weight: 400;
  margin-left: 4px;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  text-align: center;
  font-size: 12px;
}

.month-grid .dow {
  font-weight: 600;
  color: var(--muted);
  font-size: 11px;
}

.month-grid .day {
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--muted);
  background: #f1f1f5;
}

.month-grid .day.trained {
  background: var(--accent);
  color: #fff;
  font-weight: 700;
}

.month-grid .day.today {
  outline: 2px solid var(--ink);
  outline-offset: -2px;
  color: var(--text-h);
}

/* ---------- Full data list ---------- */

.data-list {
  margin-top: 16px;
  max-height: 420px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 13px;
}

.data-list table {
  width: 100%;
  border-collapse: collapse;
}

.data-list th,
.data-list td {
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);
  text-align: left;
  white-space: nowrap;
}

.data-list th {
  position: sticky;
  top: 0;
  background: #f7f7fa;
  color: var(--text-h);
  font-weight: 600;
}

.data-list td.num,
.data-list th.num {
  text-align: right;
}

/* ---------- MUI table ---------- */

.MuiTableCell-root.selected-sort,
.MuiTableCell-root.unselected-sort {
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.MuiTableCell-root.selected-sort {
  background-color: var(--accent-bg);
  color: var(--accent);
}

@media (max-width: 1000px) {
  .site-header {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 600px) {
  .card {
    padding: 16px;
  }

  .gary img {
    width: 84px;
  }

  .slogan {
    font-size: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cal-track,
  .slogan {
    transition: none;
  }
}
